/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of GameTimeFootage Inc.
 #*
 #* Copyright NextPro 2018. All Rights Reserved.
 #*
 #*===================================================================
 */

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError as _throw } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from 'environments/environment';
import { ServerResponseModel } from 'app/shared/models/serverResponse.model';
import { StorageService } from 'app/shared/services/storage.service';

@Injectable({ providedIn: 'root' })
export class UserDataService {
  private readonly TOKEN_KEY = 'nextpro_token';
  private readonly REMEMBER_ME_KEY = 'remember_me';
  private readonly SIGNUP_USER_KEY = 'remember_me';

  constructor(private httpClient: HttpClient, private storageService: StorageService) {}

  getToken() {
    return this.storageService.getCookie(this.TOKEN_KEY);
  }

  setToken(token: string, rememberMe = false) {
    // if remember me is checked then set default expiration, else expire cookie when session ends
    return this.storageService.setCookie(this.TOKEN_KEY, token, rememberMe ? null : 0);
  }

  getSignupData() {
    // get the saved signup user data
    return JSON.parse(this.atou(this.storageService.getLocalStorageItem(this.SIGNUP_USER_KEY)));
  }

  setSignupData(payload) {
    if (!payload) {
      return;
    }
    // save a base64 encoded version of the signup user data in case the user refreshes the page
    return this.storageService.setLocalStorageItem(this.SIGNUP_USER_KEY, this.utoa(JSON.stringify(payload)));
  }

  clearSignupData() {
    // clear the saved signup user data
    return this.storageService.deleteLocalStorageItem(this.SIGNUP_USER_KEY);
  }

  getUserByToken(payload): Observable<any> {
    return this.httpClient.post(`${environment.apiHostname}/v1/auth/validate-token?token=${payload}`, {}).pipe(
      map((response: ServerResponseModel) => {
        if (response.result.success) {
          return response.data;
        } else {
          return _throw(new Error('Session expired'));
        }
      })
    );
  }

  getUserById(payload): Observable<any> {
    return this.httpClient.get(`${environment.apiHostname}/v1/users/${payload}`).pipe(map(this.handleServerResponse));
  }

  updateUser(payload: any): Observable<any> {
    /* never send password field on update */
    delete payload.password;

    return this.httpClient
      .put(`${environment.apiHostname}/v1/users/${payload.id}`, {
        ...payload,
        purchases: undefined,
        inviters: undefined,
      })
      .pipe(map(this.handleServerResponse));
  }

  getUserData(): Observable<any> {
    const token = this.storageService.getCookie(this.TOKEN_KEY);
    if (token) {
      return this.httpClient
        .post(`${environment.apiHostname}/v1/auth/validate-token?token=${token}`, {})
        .pipe(map(this.handleServerResponse));
    } else {
      return _throw(new Error(''));
    }
  }

  login(payload): Observable<any> {
    // clear cart data
    return this.httpClient
      .post(`${environment.apiHostname}/v1/auth/login`, payload)
      .pipe(map(this.handleServerResponse));
  }

  loginAs(token: string): Observable<any> {
    // clear cart data
    this.storageService.deleteLocalStorageItem('plan');
    return this.httpClient
      .post(`${environment.apiHostname}/v1/auth/login-as`, { token })
      .pipe(map(this.handleServerResponse));
  }

  logout(): Observable<any> {
    this.storageService.setCookieExpiry(this.TOKEN_KEY, this.getDate(-30 * 24 * 60)); // days * hours * minutes
    this.storageService.deleteCookie(this.TOKEN_KEY);

    // clear remember me flag
    this.storageService.setCookieExpiry(this.TOKEN_KEY, this.getDate(-30 * 24 * 60)); // days * hours * minutes
    this.storageService.deleteCookie(this.REMEMBER_ME_KEY);

    // clear cart data
    this.storageService.deleteLocalStorageItem('plan');
    return new Observable((observer) => observer.next());
  }

  deleteExistingToken(): Observable<any> {
    // set expiry in the past then delete
    this.storageService.setCookieExpiry(this.TOKEN_KEY, -30 * 24 * 60);
    this.storageService.deleteCookie(this.TOKEN_KEY);
    return new Observable((observer) => observer.next());
  }

  setDraftData(userId: number, draftData = {}) {
    if (!userId) {
      return;
    }

    return this.httpClient
      .put(`${environment.apiHostname}/v1/users/set-draft/${userId}`, draftData)
      .pipe(map(this.handleServerResponse));
  }

  getUserClipsCount(userId) {
    return this.httpClient
      .get(`${environment.apiHostname}/v1/users/${userId}/clips-count`)
      .pipe(map(this.handleServerResponse));
  }

  private handleServerResponse = (response: ServerResponseModel) => {
    if (response.result.success) {
      return response.data;
    } else {
      throw new Error(response.result.message);
    }
  };

  private getDate(offsetInMinutes: number) {
    // setTime updates current value so we need let
    // tslint:disable-next-line:prefer-const
    let expireDate = new Date();
    expireDate.setTime(expireDate.getTime() + offsetInMinutes * 60 * 1000);
    return expireDate;
  }

  // ucs-2 string to base64 encoded ascii
  private utoa(str) {
    if (!str) {
      return str;
    }
    return window.btoa(unescape(encodeURIComponent(str)));
  }

  // base64 encoded ascii to ucs-2 string
  private atou(str) {
    if (!str) {
      return str;
    }
    return decodeURIComponent(escape(window.atob(str)));
  }
}
